import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mascot } from '@common-components/mascot/Mascot';
import { namespace } from '@common-config/constants';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ArrowUp, Check, ChevronDown, Loader2, MoreHorizontal, Square, X } from 'lucide-react';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AiMarkdown } from './AiMarkdown';
import { groupToolRuns, stripMcpPrefix, toolDisplayName } from './transcript';
import type { AiStreamLogEntry, AiStreamStep } from './useAiStream';
import { AiDetailLevel, DETAIL_LEVELS, useDetailLevel } from './useDetailLevel';

/**
 * The pieces every AI chat surface is made of (moved from my-bridge
 * `builder-ui.tsx`): composer, user bubble / assistant prose, the one status
 * line, tool steps, thinking block, technical log, the "Show while it works"
 * control, suggestion chips. `AiChat` assembles them.
 * Colours come only from the AI-surface tokens (`bg-ai-*`, `text-ai-*`).
 */

/**
 * "12s" / "1m 4s" from a number of seconds — a pure formatter (the ONE clock is
 * AiChat's 1 s tick). Under 3 s it returns '': a ticking "0s" reads as a timer bug.
 */
export function useFormatElapsed(): (secs: number) => string {
  const { t } = useTranslation(namespace);
  return (secs) =>
    secs < 3
      ? ''
      : secs < 60
        ? t('myBridge.builder.elapsedSec', { s: secs })
        : t('myBridge.builder.elapsedMin', {
            m: Math.floor(secs / 60),
            s: secs % 60,
          });
}

/* ── Composer ─────────────────────────────────────────────────────────────── */

export interface PromptComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder: string;
  /** The input is locked (no connection / feature off). */
  disabled?: boolean;
  /** A turn is running: typing is allowed, sending waits; the send button shows a spinner. */
  busy?: boolean;
  /**
   * Stop the running turn. When set AND `busy`, the send button becomes an
   * always-clickable Stop button (replacing the spinner) and Esc stops too.
   */
  onStop?: () => void | Promise<void>;
  /** Start side of the footer, before `footer`: e.g. an attach button. */
  leading?: React.ReactNode;
  /** Footer, start side: a hint, a connection picker, a "start over"… */
  footer?: React.ReactNode;
  sendLabel: string;
  /** 'lg' = a page, 'md' = a side panel. */
  size?: 'lg' | 'md';
  autoFocus?: boolean;
  /** The surface direction: used while the draft is empty, so the placeholder follows the UI language. */
  dir?: 'ltr' | 'rtl';
  /** Attachment chips, shown above the textarea (the attachments feature only). */
  attachmentsRow?: React.ReactNode;
  /** At least one attachment chip is ready — alone (no text) it still allows sending. */
  hasReadyAttachment?: boolean;
  /** At least one attachment chip is still uploading — blocks sending regardless of text. */
  attachmentsUploading?: boolean;
  /** A paste in the textarea carried files (e.g. a pasted image) — the attachments feature only. */
  onPasteFiles?: (files: File[]) => void;
}

export interface PromptComposerHandle {
  focus: () => void;
}

const MAX_LINES = 8;
/** Layout effect in the browser, plain effect in a server render (no warning, nothing to measure). */
const useIsoLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

/** Auto-growing textarea in a soft rounded box with a round ink send button. Enter sends (never mid-IME), Shift+Enter breaks a line. */
export const PromptComposer = forwardRef<PromptComposerHandle, PromptComposerProps>(function PromptComposer(
  {
    value,
    onChange,
    onSubmit,
    placeholder,
    disabled,
    busy,
    onStop,
    leading,
    footer,
    sendLabel,
    size = 'lg',
    autoFocus,
    dir,
    attachmentsRow,
    hasReadyAttachment,
    attachmentsUploading,
    onPasteFiles,
  },
  ref
) {
  const { t } = useTranslation(namespace);
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  useImperativeHandle(ref, () => ({ focus: () => inputRef.current?.focus() }), []);
  const canSend = !disabled && !busy && !attachmentsUploading && (value.trim() !== '' || !!hasReadyAttachment);
  const lg = size === 'lg';
  const stopping = !!(busy && onStop);

  // Grow with the content, 1..MAX_LINES lines, then scroll inside.
  useIsoLayoutEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const cs = getComputedStyle(el);
    const line = parseFloat(cs.lineHeight) || (lg ? 26 : 22);
    const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const max = line * MAX_LINES + pad;
    // Empty: one line (a long placeholder must not grow the box — scrollHeight counts it).
    const content = value === '' ? line + pad : el.scrollHeight;
    el.style.height = `${Math.min(content, max)}px`;
    el.style.overflowY = content > max ? 'auto' : 'hidden';
  }, [value, lg]);

  const submit = useCallback(() => {
    if (canSend) onSubmit();
  }, [canSend, onSubmit]);

  return (
    <div
      // Rest: a hairline + a soft shadow. Focus: the same hairline + a faint mint glow (not a stroke).
      className={`relative border border-ai-border-strong bg-ai-surface transition-shadow duration-(--ai-duration) ease-ai ${
        lg
          ? 'rounded-ai-composer shadow-[0_10px_40px_-20px_rgba(0,0,0,0.35)] focus-within:shadow-[0_10px_40px_-20px_rgba(0,0,0,0.35),0_0_0_4px_color-mix(in_srgb,var(--ai-accent)_16%,transparent)]'
          : 'rounded-[22px] shadow-[0_6px_24px_-16px_rgba(0,0,0,0.35)] focus-within:shadow-[0_6px_24px_-16px_rgba(0,0,0,0.35),0_0_0_4px_color-mix(in_srgb,var(--ai-accent)_16%,transparent)]'
      }`}
      onClick={() => inputRef.current?.focus()}
      data-testid="ai-composer"
    >
      {attachmentsRow && <div className={`flex flex-wrap gap-1.5 ${lg ? 'px-4 pt-3' : 'px-3 pt-2.5'}`}>{attachmentsRow}</div>}
      {/* `!bg-transparent`: the web app's unlayered `body textarea { background: var(--color-surface) }` beats layered utilities. */}
      <textarea
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onPaste={
          onPasteFiles
            ? (e) => {
                const files = Array.from(e.clipboardData?.files ?? []);
                if (files.length) onPasteFiles(files);
              }
            : undefined
        }
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            if (!stopping) return;
            e.preventDefault();
            void onStop?.();
            return;
          }
          // IME: Enter confirms a composition (Hebrew/Arabic/CJK keyboards) — never send then.
          if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing || e.keyCode === 229) return;
          e.preventDefault();
          submit();
        }}
        placeholder={placeholder}
        aria-label={placeholder}
        rows={1}
        disabled={disabled}
        autoFocus={autoFocus}
        // Empty: the surface direction (a Hebrew placeholder must not render LTR); typed text: per its own script.
        dir={value ? 'auto' : dir}
        className={`block w-full resize-none border-0 !bg-transparent !text-ai-text shadow-none outline-none placeholder:text-ai-text-faint disabled:opacity-60 ${
          lg ? 'px-5 pt-4 pb-6 text-[16px] leading-[26px]' : 'px-4 pt-3 pb-4 text-[14px] leading-[22px]'
        }`}
        data-testid="ai-composer-input"
      />
      <div className={`flex items-center justify-between gap-2 ${lg ? 'px-2.5 pb-2.5' : 'px-2 pb-2'}`}>
        <div className="flex min-w-0 items-center gap-1.5 ps-1">
          {leading}
          {footer}
        </div>
        {stopping ? (
          <Button
            type="button"
            variant="ai"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              void onStop?.();
            }}
            aria-label={t('ai.chat.stop', 'Stop')}
            className={`${lg ? 'h-10 w-10' : 'h-9 w-9'} rounded-full`}
            data-testid="ai-stop"
          >
            <Square className={lg ? 'size-4' : 'size-3.5'} fill="currentColor" />
          </Button>
        ) : (
          <Button
            type="button"
            variant="ai"
            size="icon"
            onClick={(e) => {
              e.stopPropagation();
              submit();
            }}
            disabled={!canSend}
            aria-label={sendLabel}
            className={`${lg ? 'h-10 w-10' : 'h-9 w-9'} disabled:opacity-100 ${busy ? '' : 'disabled:bg-ai-muted disabled:text-ai-text-faint disabled:shadow-none'}`}
            data-testid="ai-send"
          >
            {busy ? (
              <Loader2 className={lg ? 'size-5 animate-spin' : 'size-4 animate-spin'} />
            ) : (
              <ArrowUp className={lg ? 'size-5' : 'size-4'} strokeWidth={2.4} />
            )}
          </Button>
        )}
      </div>
    </div>
  );
});

/** ON colour for switches on the AI surfaces (the shared Switch's `bg-primary` is undefined there). */
export const AI_SWITCH_ON_CLASS = 'data-[state=checked]:bg-ai-accent data-[state=checked]:border-ai-accent';

/** Each level's label + one-line hint (key tables: the i18n guard checks every one). */
const DETAIL_LEVEL_COPY: Record<AiDetailLevel, { labelKey: string; hintKey: string }> = {
  quiet: { labelKey: 'ai.detail.quiet', hintKey: 'ai.detail.quietHint' },
  activity: { labelKey: 'ai.detail.activity', hintKey: 'ai.detail.activityHint' },
  thinking: { labelKey: 'ai.detail.thinking', hintKey: 'ai.detail.thinkingHint' },
  trace: { labelKey: 'ai.detail.trace', hintKey: 'ai.detail.traceHint' },
};

/**
 * "Show while it works": the ONE control for how much of a running turn is shown
 * (Quiet / Activity / Thinking / Technical). Every level streams; the level only
 * filters the view. Same key on every surface (`bt.ai.detail`).
 */
export const DetailLevelSelect: React.FC<{ dir?: 'ltr' | 'rtl' }> = ({ dir }) => {
  const { t } = useTranslation(namespace);
  const [level, setLevel] = useDetailLevel();
  const id = 'ai-detail-level';
  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ai-text">
        {t('ai.detail.title')}
      </label>
      <Select value={level} onValueChange={(v) => setLevel(v as AiDetailLevel)} dir={dir}>
        <SelectTrigger
          id={id}
          className="h-9 w-full rounded-ai-sm border-ai-border-strong bg-ai-surface text-ai-text focus-visible:ring-ai-focus-ring"
          data-testid="ai-detail-level"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent
          className="w-[var(--radix-select-trigger-width)] max-w-[calc(100vw-24px)] rounded-ai-sm"
          style={{ backgroundColor: 'var(--ai-surface)', color: 'var(--ai-text)', borderColor: 'var(--ai-border-strong)' }}
        >
          {DETAIL_LEVELS.map((value) => (
            <SelectPrimitive.Item
              key={value}
              value={value}
              className="relative flex w-full cursor-default select-none flex-col items-start gap-0.5 rounded-sm py-1.5 ps-2 pe-8 text-start outline-hidden data-[highlighted]:bg-ai-muted"
              data-testid={`ai-detail-level-${value}`}
            >
              <span className="absolute end-2 top-2 flex size-3.5 items-center justify-center">
                <SelectPrimitive.ItemIndicator>
                  <Check className="size-4 text-ai-accent" />
                </SelectPrimitive.ItemIndicator>
              </span>
              <SelectPrimitive.ItemText>
                <span className="text-sm text-ai-text">{t(DETAIL_LEVEL_COPY[value].labelKey)}</span>
              </SelectPrimitive.ItemText>
              <span className="text-xs leading-snug text-ai-text-secondary">{t(DETAIL_LEVEL_COPY[value].hintKey)}</span>
            </SelectPrimitive.Item>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
};

/**
 * THE ⋯ menu of an AI chat: "Show while it works" first, then the surface's own
 * entries. AiChat renders it in its header; a page that hosts the chat's controls
 * in its own header (the agent workspace) renders the same one there.
 */
export const ChatMenu: React.FC<{ dir?: 'ltr' | 'rtl'; children?: React.ReactNode; testId?: string; align?: 'start' | 'end' }> = ({
  dir,
  children,
  testId = 'ai-chat-menu',
  align = 'end',
}) => {
  const { t } = useTranslation(namespace);
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('ai.chat.more')}
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-ai-text-secondary transition-colors hover:bg-ai-muted hover:text-ai-text outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
          data-testid={testId}
        >
          <MoreHorizontal className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        className="w-80 max-w-[calc(100vw-24px)] max-h-[min(80vh,var(--radix-popover-content-available-height))] overflow-y-auto rounded-ai-md p-3"
        style={{ backgroundColor: 'var(--ai-surface)', color: 'var(--ai-text)', borderColor: 'var(--ai-border-strong)' }}
      >
        <div dir={dir} className="flex flex-col gap-3">
          <DetailLevelSelect dir={dir} />
          {children}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/** What the agent wrote before calling a tool: a quiet line in the live turn (the saved reply replaces it). */
export const NarrationLine: React.FC<{ text: string }> = ({ text }) => (
  <p dir="auto" className="whitespace-pre-wrap text-[13px] leading-5 text-ai-text-secondary" data-testid="ai-narration">
    {text}
  </p>
);

/* ── Conversation ─────────────────────────────────────────────────────────── */

/**
 * One turn. The user's: a soft neutral bubble on the end side (plain text,
 * never Markdown). The AI's: full-width prose, no bubble — a string renders as
 * Markdown.
 */
export const ChatBubble: React.FC<{
  mine: boolean;
  children: React.ReactNode;
  size?: 'lg' | 'md';
}> = ({ mine, children, size = 'lg' }) => {
  const lg = size === 'lg';
  if (mine) {
    return (
      <div
        dir="auto"
        className={`max-w-[80%] self-end whitespace-pre-wrap break-words rounded-ai-bubble bg-ai-muted text-start text-ai-text animate-in fade-in slide-in-from-bottom-2 duration-(--ai-duration-enter) ease-ai ${
          lg ? 'px-4 py-2.5 text-[16px] leading-[26px]' : 'px-3.5 py-2 text-[14px] leading-[22px]'
        }`}
        data-testid="ai-msg-user"
      >
        {children}
      </div>
    );
  }
  return (
    <div
      dir="auto"
      className={`self-stretch text-start text-ai-text ${lg ? 'text-[16px] leading-[28px]' : 'text-[14px] leading-[23px]'}`}
      data-testid="ai-msg-assistant"
    >
      {typeof children === 'string' ? <AiMarkdown text={children} /> : children}
    </div>
  );
};

/**
 * THE status line — the only "the AI is working" indicator (rev 3.3 §4): the
 * thinking face, what is happening now (always the latest backend chunk, or the
 * browser's own "Sending…" / "Connection quiet…"), the step time and the turn time.
 * Never bare dots. Under the last live item while a turn runs; gone after it settles.
 */
export const AiStatusLine: React.FC<{
  label: string;
  /** Time in the current step ('' under 3 s). */
  step?: string;
  /** Time since the turn started ('' when it would repeat the step time). */
  total?: string;
  tone?: 'default' | 'ok' | 'error' | 'quiet';
}> = ({ label, step, total, tone = 'default' }) => (
  <div
    className="flex min-w-0 items-center gap-2.5 self-stretch animate-in fade-in duration-(--ai-duration) ease-ai"
    data-testid="ai-status"
    data-tone={tone}
  >
    <Mascot size={24} mood="thinking" level="ambient" />
    <span
      className={`min-w-0 truncate text-[14px] font-medium ${tone === 'error' ? 'text-ai-danger' : 'text-ai-text-secondary'}`}
      aria-live="polite"
      data-testid="ai-status-label"
    >
      {label}
      {step && (
        <span className="font-normal tabular-nums text-ai-text-faint">
          {' · '}
          <bdi>{step}</bdi>
        </span>
      )}
    </span>
    {total && (
      <span className="ms-auto shrink-0 text-xs tabular-nums text-ai-text-faint" data-testid="ai-status-total">
        <bdi>{total}</bdi>
      </span>
    )}
  </div>
);

/**
 * The model's summarized thinking (Thinking level, live only — never saved): a
 * muted block that streams while it thinks, then folds to "Thought for 14s ▸".
 */
export const ThinkingBlock: React.FC<{ text: string; open: boolean; seconds: number }> = ({ text, open, seconds }) => {
  const { t } = useTranslation(namespace);
  const elapsed = useFormatElapsed()(seconds);
  const [expanded, setExpanded] = useState(false);
  const body = (
    <p dir="auto" className="whitespace-pre-wrap border-s-2 border-ai-border ps-3 text-[13px] italic leading-5 text-ai-text-secondary">
      {text}
    </p>
  );
  if (open) {
    return (
      <div className="self-stretch" data-testid="ai-thinking-text" data-open="true">
        {body}
      </div>
    );
  }
  return (
    <div className="self-stretch" data-testid="ai-thinking-text" data-open="false">
      <button
        type="button"
        onClick={() => setExpanded((e) => !e)}
        aria-expanded={expanded}
        className="inline-flex items-center gap-1 rounded-full px-1 py-0.5 text-[12.5px] text-ai-text-faint transition-colors hover:text-ai-text outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
      >
        <span>{elapsed ? t('ai.status.thoughtFor', { time: elapsed }) : t('ai.status.thought')}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-(--ai-duration) ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && <div className="mt-1">{body}</div>}
    </div>
  );
};

const secs1 = (ms: number) => `${(Math.max(0, ms) / 1000).toFixed(1)} s`;
const size = (bytes: number) => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`);

/**
 * The Technical level's log (live only): every backend phase with its pod and
 * detail, every tool with its arguments (≤2 KB), ok / error, result size and
 * duration — each stamped with its time since the turn started. Phase names, pods
 * and details are shown as sent; tools by the same readable name as everywhere else.
 */
export const TraceLog: React.FC<{ steps: AiStreamStep[]; log: AiStreamLogEntry[]; startedAt: number | null; now: number }> = ({
  steps,
  log,
  startedAt,
  now,
}) => {
  const { t } = useTranslation(namespace);
  const toolLabel = useToolLabel(); // the ONE tool-name helper (chips, status line, log)
  if (log.length === 0) return null;
  const t0 = startedAt ?? log[0].at;
  const byId = new Map(steps.map((s) => [s.id, s]));
  return (
    <details className="self-stretch rounded-ai-sm border border-ai-border bg-ai-muted px-3 py-2" data-testid="ai-trace" dir="ltr">
      <summary className="cursor-pointer select-none text-[12.5px] font-medium text-ai-text-secondary outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring">
        {t('ai.trace.title')} <span className="tabular-nums text-ai-text-faint">({log.length})</span>
      </summary>
      <ol className="mt-2 flex flex-col gap-1.5 font-mono text-[11.5px] leading-4 text-ai-text-secondary">
        {log.map((entry) => {
          const stamp = <span className="w-14 shrink-0 tabular-nums text-ai-text-faint">+{secs1(entry.at - t0)}</span>;
          if (entry.kind === 'phase') {
            return (
              <li key={entry.id} className="flex gap-2" data-testid="ai-trace-phase">
                {stamp}
                <span className="min-w-0 break-words">
                  <span className="text-ai-text">{entry.phase}</span>
                  {entry.pod && <span> · {t('ai.trace.pod')}: {entry.pod}</span>}
                  {entry.detail && <span> · {entry.detail}</span>}
                </span>
              </li>
            );
          }
          const step = byId.get(entry.stepId);
          if (!step || step.kind !== 'tool') return null;
          const status = step.endedAt === undefined ? t('ai.trace.running') : step.isError ? t('ai.trace.error') : t('ai.trace.ok');
          return (
            <li key={entry.id} className="flex gap-2" data-testid="ai-trace-tool" data-error={step.isError ? 'true' : undefined}>
              {stamp}
              <span className="flex min-w-0 flex-col gap-1">
                <span className="break-words">
                  <span className="text-ai-text">{toolLabel(step.name)}</span>
                  {' · '}
                  <span className={step.isError ? 'text-ai-danger' : undefined}>{status}</span>
                  {step.bytes !== undefined && <span> · {size(step.bytes)}</span>}
                  <span> · {secs1((step.endedAt ?? now) - step.startedAt)}</span>
                </span>
                {step.input && (
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-sm bg-ai-surface p-1.5 text-[11px]" data-testid="ai-trace-input">
                    {step.input}
                  </pre>
                )}
              </span>
            </li>
          );
        })}
      </ol>
    </details>
  );
};

/**
 * A tool's label: a translated name when `ai.tools.<name>` exists, else the readable raw name.
 * Follow-up (QA F3, accepted): `ai.tools.*` exists in en + he-IL only; the other 7 locales
 * fall back to the English labels (as do `myBridge.builder.elapsedSec/elapsedMin`).
 */
export function useToolLabel(): (raw: string) => string {
  const { t } = useTranslation(namespace);
  return (raw) =>
    t(`ai.tools.${stripMcpPrefix(raw)}`, {
      defaultValue: toolDisplayName(raw),
    }); // i18n-dynamic-key: ai.tools.* with a fallback
}

/** One finished tool step, by name only ("Checked contacts"), "×2" for a repeated call, marked when it failed. The running step is the status line's label. */
export const ToolChip: React.FC<{ name: string; count?: number; failed?: boolean }> = ({ name, count = 1, failed }) => {
  const { t } = useTranslation(namespace);
  const tool = useToolLabel()(name);
  return (
    <div
      className="flex items-center gap-2 font-mono text-[12.5px] text-ai-text-secondary"
      data-testid="ai-activity"
      data-failed={failed ? 'true' : undefined}
    >
      {failed ? (
        <X className="h-3.5 w-3.5 shrink-0 text-ai-danger" strokeWidth={2.6} />
      ) : (
        <Check className="h-3.5 w-3.5 shrink-0 text-ai-accent" strokeWidth={2.6} />
      )}
      <span dir="auto" className="truncate">
        {failed ? t('ai.chat.toolFailed', { tool }) : t('ai.chat.toolDone', { tool })}
      </span>
      {count > 1 && <span className="shrink-0 tabular-nums text-ai-text-faint">×{count}</span>}
    </div>
  );
};

/** A settled run of tools, collapsed to one quiet line ("Used 3 tools ▸"); click to list them. */
export const ToolSummary: React.FC<{ names: string[] }> = ({ names }) => {
  const { t } = useTranslation(namespace);
  const [open, setOpen] = useState(false);
  if (names.length === 0) return null;
  return (
    <div className="self-stretch" data-testid="ai-tools-summary">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-full px-1 py-0.5 text-[12.5px] text-ai-text-secondary transition-colors hover:text-ai-text outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring"
      >
        <span>{t('ai.chat.usedTools', { count: names.length })}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-(--ai-duration) ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="mt-1.5 flex flex-col gap-1 border-s border-ai-border ps-3">
          {groupToolRuns(names).map((run, i) => (
            <ToolChip key={`${run.name}-${i}`} name={run.name} count={run.count} />
          ))}
        </div>
      )}
    </div>
  );
};

/** One-click example prompts: pick → fills the composer. */
export const SuggestionChips: React.FC<{
  items: string[];
  onPick: (s: string) => void;
  size?: 'lg' | 'md';
}> = ({ items, onPick, size = 'lg' }) => (
  <div className="mx-auto flex max-w-2xl flex-wrap justify-center gap-2">
    {items.map((s) => (
      <button
        key={s}
        type="button"
        onClick={() => onPick(s)}
        dir="auto"
        className={`rounded-full border border-ai-border bg-ai-surface text-ai-text transition-all duration-(--ai-duration-fast) ease-ai hover:-translate-y-0.5 hover:border-ai-border-strong outline-none focus-visible:ring-2 focus-visible:ring-ai-focus-ring ${
          size === 'lg' ? 'px-4 py-2 text-sm' : 'px-3 py-1.5 text-xs'
        }`}
      >
        {s}
      </button>
    ))}
  </div>
);
